/**
 * 地理定位服務
 * 提供瀏覽器地理定位功能和相關工具函數
 */

/**
 * 獲取用戶當前位置
 * @param {Object} options - 地理定位選項
 * @returns {Promise<Object>} 位置信息 { latitude, longitude, accuracy }
 */
export const getCurrentPosition = (options = {}) => {
  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000 // 5分鐘緩存
  };

  const finalOptions = { ...defaultOptions, ...options };

  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('此瀏覽器不支援地理定位功能'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        resolve({
          latitude,
          longitude,
          accuracy,
          timestamp: position.timestamp
        });
      },
      (error) => {
        let errorMessage = '無法獲取位置信息';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = '用戶拒絕了地理定位請求';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = '位置信息不可用';
            break;
          case error.TIMEOUT:
            errorMessage = '獲取位置信息超時';
            break;
          default:
            errorMessage = '未知的地理定位錯誤';
            break;
        }
        
        reject(new Error(errorMessage));
      },
      finalOptions
    );
  });
};

/**
 * 監聽位置變化
 * @param {Function} callback - 位置變化回調函數
 * @param {Object} options - 地理定位選項
 * @returns {number} watchId
 */
export const watchPosition = (callback, options = {}) => {
  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000 // 1分鐘緩存
  };

  const finalOptions = { ...defaultOptions, ...options };

  if (!navigator.geolocation) {
    throw new Error('此瀏覽器不支援地理定位功能');
  }

  return navigator.geolocation.watchPosition(
    (position) => {
      const { latitude, longitude, accuracy } = position.coords;
      callback({
        latitude,
        longitude,
        accuracy,
        timestamp: position.timestamp
      });
    },
    (error) => {
      console.error('位置監聽錯誤:', error);
    },
    finalOptions
  );
};

/**
 * 停止位置監聽
 * @param {number} watchId - 監聽ID
 */
export const clearWatch = (watchId) => {
  if (navigator.geolocation && watchId) {
    navigator.geolocation.clearWatch(watchId);
  }
};

/**
 * 計算兩點之間的距離（使用 Haversine 公式）
 * @param {number} lat1 - 第一個點的緯度
 * @param {number} lon1 - 第一個點的經度
 * @param {number} lat2 - 第二個點的緯度
 * @param {number} lon2 - 第二個點的經度
 * @returns {number} 距離（米）
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // 地球半徑（米）
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // 距離（米）
};

/**
 * 格式化距離顯示
 * @param {number} distance - 距離（米）
 * @returns {string} 格式化的距離字符串
 */
export const formatDistance = (distance) => {
  if (distance < 1000) {
    return `${Math.round(distance)} 米`;
  } else {
    return `${(distance / 1000).toFixed(1)} 公里`;
  }
};

/**
 * 檢查地理定位權限狀態
 * @returns {Promise<string>} 權限狀態 ('granted', 'denied', 'prompt')
 */
export const checkGeolocationPermission = async () => {
  if (!navigator.permissions) {
    return 'prompt'; // 不支援權限API，需要嘗試獲取位置
  }

  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    return permission.state;
  } catch (error) {
    console.warn('無法檢查地理定位權限:', error);
    return 'prompt';
  }
};

/**
 * 請求地理定位權限
 * @returns {Promise<boolean>} 是否獲得權限
 */
export const requestGeolocationPermission = async () => {
  try {
    await getCurrentPosition({ timeout: 5000 });
    return true;
  } catch (error) {
    return false;
  }
};
